import React, {Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {EdgesGeometry, Path, Vector2} from "three";
import {useTexture} from "@react-three/drei";
import {createShape} from "../../../customer/processing/DetailProcessing/Canvas/CanvasCalcPositions/createShape";
import {
    applyDetailTextureTransform,
    DEFAULT_TEXTURE_ROTATION_DEG,
    DEFAULT_TEXTURE_SCALE_MM,
    resolveDetailTextureUrl,
    TEST_TEXTURES,
} from "./detailTextures";
import {
    classifyDetailHoles,
    getBlindFaceHolePose,
    getHoleAxesPose,
    getSideHolePose,
} from "./detailHoles";
import {
    classifyDetailGrooves,
    getBlindFaceGroovePose,
    grooveContourShape,
} from "./detailGrooves";
import {
    classifyDetailOtherMachining,
    otherThroughContourShape,
    otherThroughRoundPath,
} from "./detailOtherMachining";
import {MM_TO_M} from "./panelMeshTransform";
import {orderDetailContourPoints} from "../../../../utils/processing/orderContourPoints";

export {MM_TO_M, makePanelMeshLocalMatrix, panelCornerMmToInstance} from "./panelMeshTransform";

const CATALOG_URLS = TEST_TEXTURES.map((t) => t.url);

/** useTexture / Suspense бросают Event при битой картинке или CORS — не роняем всю сцену. */
class PanelTextureErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError() {
        return {hasError: true};
    }

    componentDidUpdate(prevProps) {
        if (prevProps.textureUrl !== this.props.textureUrl) {
            this.setState({hasError: false});
        }
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback;
        }
        return this.props.children;
    }
}

const HOLE_CAVITY_COLOR = "#2c2c2c";
const HOLE_RIM_COLOR = "#1a1a1a";
const GROOVE_CAVITY_COLOR = "#3a3a3a";

const normalizeContourPoints = (detailData) => {
    const ordered = orderDetailContourPoints(detailData);
    if (!ordered?.length) {
        return ordered;
    }
    const normalized = ordered.map((p) => ({
        ...p,
        x: Number(p?.x) || 0,
        y: Number(p?.y) || 0,
    }));
    if (normalized.length > 1) {
        const first = normalized[0];
        const last = normalized[normalized.length - 1];
        if (first.x === last.x && first.y === last.y) {
            normalized.pop();
        }
    }
    return normalized;
};

const buildContourShape = (detailData) => {
    // by-module-id: points без sN и вразнобой — обход по edges[].sequenceNumber
    const points = normalizeContourPoints(detailData);
    if (points?.length) {
        return createShape(points);
    }

    const length = Number(detailData?.length) || 0;
    const width = Number(detailData?.width) || 0;
    return createShape([
        {x: 0, y: 0},
        {x: length, y: 0},
        {x: length, y: width},
        {x: 0, y: width},
        {x: 0, y: 0},
    ]);
};

/**
 * ExtrudeGeometry (three ≥0.15x) passes `vertices` as a flat xyz array.
 * Normalize shape mm → 0…1 so the map shows a real pattern, not one texel.
 */
const createPanelUVGenerator = (lengthMm, widthMm, heightMm) => {
    const lx = Math.max(lengthMm, 1);
    const ly = Math.max(widthMm, 1);
    const lz = Math.max(heightMm, 1);

    return {
        generateTopUV(_geometry, vertices, indexA, indexB, indexC) {
            const ax = vertices[indexA * 3];
            const ay = vertices[indexA * 3 + 1];
            const bx = vertices[indexB * 3];
            const by = vertices[indexB * 3 + 1];
            const cx = vertices[indexC * 3];
            const cy = vertices[indexC * 3 + 1];
            return [
                new Vector2(ax / lx, ay / ly),
                new Vector2(bx / lx, by / ly),
                new Vector2(cx / lx, cy / ly),
            ];
        },
        generateSideWallUV(_geometry, vertices, indexA, indexB, indexC, indexD) {
            const ax = vertices[indexA * 3];
            const ay = vertices[indexA * 3 + 1];
            const az = vertices[indexA * 3 + 2];
            const bx = vertices[indexB * 3];
            const by = vertices[indexB * 3 + 1];
            const bz = vertices[indexB * 3 + 2];
            const cx = vertices[indexC * 3];
            const cy = vertices[indexC * 3 + 1];
            const cz = vertices[indexC * 3 + 2];
            const dx = vertices[indexD * 3];
            const dy = vertices[indexD * 3 + 1];
            const dz = vertices[indexD * 3 + 2];

            if (Math.abs(ay - by) < Math.abs(ax - bx)) {
                return [
                    new Vector2(ax / lx, 1 - az / lz),
                    new Vector2(bx / lx, 1 - bz / lz),
                    new Vector2(cx / lx, 1 - cz / lz),
                    new Vector2(dx / lx, 1 - dz / lz),
                ];
            }
            return [
                new Vector2(ay / ly, 1 - az / lz),
                new Vector2(by / ly, 1 - bz / lz),
                new Vector2(cy / ly, 1 - cz / lz),
                new Vector2(dy / ly, 1 - dz / lz),
            ];
        },
    };
};

const SHELL_OPACITY = 0.22;

const shellMaterialProps = (shellTransparent) => (
    shellTransparent
        ? {transparent: true, opacity: SHELL_OPACITY, depthWrite: false}
        : {transparent: false, opacity: 1, depthWrite: true}
);

/** Native LineSegments: drei Edges (Line2) исчезает при отрицательном scale зеркала. */
const MeshContourEdges = ({color = "black", threshold = 1}) => {
    const lineRef = useRef(null);
    const sourceRef = useRef(null);
    const thresholdRef = useRef(threshold);

    useLayoutEffect(() => {
        const line = lineRef.current;
        const source = line?.parent?.geometry;
        if (!line || !source) return;
        if (sourceRef.current === source && thresholdRef.current === threshold) return;
        sourceRef.current = source;
        thresholdRef.current = threshold;
        const edges = new EdgesGeometry(source, threshold);
        const previous = line.geometry;
        line.geometry = edges;
        if (previous && previous !== edges) previous.dispose();
    });

    useEffect(() => () => {
        lineRef.current?.geometry?.dispose();
    }, []);

    return (
        <lineSegments ref={lineRef} raycast={() => null} renderOrder={4}>
            <bufferGeometry/>
            <lineBasicMaterial color={color} toneMapped={false}/>
        </lineSegments>
    );
};

/** Clone + transform per panel (shared catalog textures must not share repeat/rotation). */
const CatalogPanelMaterial = ({
    textureUrl,
    lengthMm,
    widthMm,
    scaleMm,
    rotationDeg,
    selected,
    shellTransparent = false,
    polygonOffsetFactor = 1,
    polygonOffsetUnits = 1,
}) => {
    const maps = useTexture(CATALOG_URLS);
    const baseTexture = useMemo(() => {
        const idx = CATALOG_URLS.indexOf(textureUrl);
        return maps[idx >= 0 ? idx : 0];
    }, [maps, textureUrl]);

    const map = useMemo(() => {
        const cloned = baseTexture.clone();
        cloned.needsUpdate = true;
        return cloned;
    }, [baseTexture]);

    useLayoutEffect(() => {
        applyDetailTextureTransform(
            map,
            {length: lengthMm, width: widthMm},
            {scaleMm, rotationDeg},
        );
    }, [map, lengthMm, widthMm, scaleMm, rotationDeg]);

    return (
        <meshStandardMaterial
            attach="material"
            map={map}
            color={selected ? "#C8D4E8" : "#ffffff"}
            roughness={0.85}
            metalness={0.05}
            side={2}
            dispose={null}
            polygonOffset
            polygonOffsetFactor={polygonOffsetFactor}
            polygonOffsetUnits={polygonOffsetUnits}
            {...shellMaterialProps(shellTransparent)}
        />
    );
};

const ExternalPanelMaterial = ({
    textureUrl,
    lengthMm,
    widthMm,
    scaleMm,
    rotationDeg,
    selected,
    shellTransparent = false,
    polygonOffsetFactor = 1,
    polygonOffsetUnits = 1,
}) => {
    const baseTexture = useTexture(textureUrl);

    const map = useMemo(() => {
        const cloned = baseTexture.clone();
        cloned.needsUpdate = true;
        return cloned;
    }, [baseTexture]);

    useLayoutEffect(() => {
        applyDetailTextureTransform(
            map,
            {length: lengthMm, width: widthMm},
            {scaleMm, rotationDeg},
        );
    }, [map, lengthMm, widthMm, scaleMm, rotationDeg]);

    return (
        <meshStandardMaterial
            attach="material"
            map={map}
            color={selected ? "#C8D4E8" : "#ffffff"}
            roughness={0.85}
            metalness={0.05}
            side={2}
            dispose={null}
            polygonOffset
            polygonOffsetFactor={polygonOffsetFactor}
            polygonOffsetUnits={polygonOffsetUnits}
            {...shellMaterialProps(shellTransparent)}
        />
    );
};

/** Одна центральная ось отверстия: тонкий стержень со стрелкой на одном конце. */
const HoleMiniAxes = ({position, rotation = [Math.PI / 2, 0, 0], axisLength}) => {
    const length = Math.max(Number(axisLength) || 14, 1);
    const shaftRadius = Math.max(length * 0.012, 0.12);
    const headLength = Math.max(length * 0.22, 1.6);
    const headRadius = Math.max(shaftRadius * 3.2, 0.38);
    const shaftLength = Math.max(length - headLength, length * 0.55);
    const color = "#1565c0";
    // Стержень смещён: основание у −Y, стрелка на +Y (наружу от входа / вдоль оси).
    const shaftCenterY = -headLength / 2;

    return (
        <group position={position} rotation={rotation} userData={{module3dRole: "helper"}}>
            <mesh position={[0, shaftCenterY, 0]} raycast={() => null} renderOrder={3}>
                <cylinderGeometry args={[shaftRadius, shaftRadius, shaftLength, 6]}/>
                <meshBasicMaterial color={color}/>
            </mesh>
            <mesh
                position={[0, shaftLength / 2 + headLength / 2 + shaftCenterY, 0]}
                raycast={() => null}
                renderOrder={3}
            >
                <coneGeometry args={[headRadius, headLength, 8]}/>
                <meshBasicMaterial color={color}/>
            </mesh>
        </group>
    );
};

const HoleCavityMesh = ({pose, shellTransparent = false}) => {
    if (!pose) return null;
    return (
        <mesh
            position={pose.position}
            rotation={pose.rotation}
            renderOrder={2}
        >
            <cylinderGeometry
                args={[
                    pose.radius,
                    pose.radius,
                    pose.depth,
                    shellTransparent ? 24 : 20,
                    1,
                    Boolean(shellTransparent),
                ]}
            />
            <meshStandardMaterial
                color={shellTransparent ? "#ffffff" : HOLE_CAVITY_COLOR}
                roughness={shellTransparent ? 0.85 : 1}
                metalness={shellTransparent ? 0.05 : 0}
                side={2}
                polygonOffset
                polygonOffsetFactor={-1}
                polygonOffsetUnits={-1}
                {...shellMaterialProps(shellTransparent)}
            />
            {shellTransparent ? <MeshContourEdges color="black" threshold={18}/> : null}
        </mesh>
    );
};

const GrooveCavityMesh = ({pose, shellTransparent = false}) => {
    if (!pose?.shape) return null;
    return (
        <mesh position={[0, 0, pose.z]} renderOrder={2}>
            <extrudeGeometry
                args={[pose.shape, {steps: 1, depth: pose.depth, bevelEnabled: false}]}
            />
            <meshStandardMaterial
                color={shellTransparent ? "#ffffff" : GROOVE_CAVITY_COLOR}
                roughness={shellTransparent ? 0.85 : 1}
                metalness={shellTransparent ? 0.05 : 0}
                side={2}
                polygonOffset
                polygonOffsetFactor={-1}
                polygonOffsetUnits={-1}
                {...shellMaterialProps(shellTransparent)}
            />
            {shellTransparent ? <MeshContourEdges color="black" threshold={1}/> : null}
        </mesh>
    );
};
const BlindFaceEntranceDisk = ({hole, heightMm, shellTransparent = false}) => {
    const face = hole?.contour?.position === "BOTTOM" ? "BOTTOM" : "TOP";
    const x = Number(hole.center?.x) || 0;
    const y = Number(hole.center?.y) || 0;
    const r = Number(hole.radius) || 0;
    const z = face === "BOTTOM" ? 0.05 : Math.max(Number(heightMm) || 18, 1) - 0.05;

    return (
        <mesh position={[x, y, z]} rotation={[0, 0, 0]} renderOrder={3}>
            {shellTransparent
                ? <ringGeometry args={[Math.max(0, r * 0.88), r, 24]}/>
                : <circleGeometry args={[r, 24]}/>}
            <meshStandardMaterial
                color={shellTransparent ? "#ffffff" : HOLE_RIM_COLOR}
                roughness={shellTransparent ? 0.85 : 1}
                metalness={shellTransparent ? 0.05 : 0}
                side={2}
                polygonOffset
                polygonOffsetFactor={-2}
                polygonOffsetUnits={-2}
                {...shellMaterialProps(shellTransparent)}
            />
        </mesh>
    );
};

/**
 * Contour in XY (mm); extrude +Z and rotateX(−π/2).
 * Width then points toward −Z, so the group is shifted by its width.
 * Instance local matches the cube: +X/+Y/+Z from the origin into the panel.
 *
 * Holes:
 * - through TOP/BOTTOM → shape.holes (real cut in extrude)
 * - blind TOP/BOTTOM → cavity cylinder + entrance disk
 * - LEFT/RIGHT/FRONT/BACK → cavity cylinder from the edge
 *
 * Grooves:
 * - through TOP/BOTTOM or side contour → shape.holes
 * - blind TOP/BOTTOM → extruded cavity from the face
 *
 * Other (notches / quarters / roundNotches):
 * - through contour or round → shape.holes
 * - blind → cavity extrude / cylinder
 */
const ModuleDetailMesh = ({
                              detailData,
                              textureUrl: textureUrlProp,
                              textureScaleMm = DEFAULT_TEXTURE_SCALE_MM,
                              textureRotationDeg = DEFAULT_TEXTURE_ROTATION_DEG,
                              selected = false,
                              showHoles = true,
                              showGrooves = true,
                              showOtherMachining = true,
                              shellTransparent = false,
                              /** Порядок отрисовки на сцене (по sN / sequenceNumber детали). */
                              renderOrder = 0,
                              onSelect,
                              onFreeRotationPointerDown,
                          }) => {
    const height = Number(detailData?.height) || 18;
    const lengthMm = Number(detailData?.length) || 1000;
    const widthMm = Number(detailData?.width) || 1000;

    // Сначала рисуем корпус, отверстия/пазы — на следующем кадре (иначе первый клик «зависает»).
    const extrasKey = detailData?.id ?? detailData?.name ?? "mesh";
    const [extrasReady, setExtrasReady] = useState(false);
    useEffect(() => {
        setExtrasReady(false);
        let innerId = 0;
        const outerId = window.requestAnimationFrame(() => {
            innerId = window.requestAnimationFrame(() => setExtrasReady(true));
        });
        return () => {
            window.cancelAnimationFrame(outerId);
            window.cancelAnimationFrame(innerId);
        };
    }, [extrasKey]);

    const holesOn = showHoles && extrasReady;
    const groovesOn = showGrooves && extrasReady;
    const otherOn = showOtherMachining && extrasReady;

    const textureUrl = useMemo(() => {
        // undefined — старое поведение (подбор по материалу);
        // null / "" — явно без текстуры.
        if (textureUrlProp === null || textureUrlProp === "") return null;
        if (textureUrlProp) return textureUrlProp;
        return resolveDetailTextureUrl(detailData);
    }, [textureUrlProp, detailData]);
    const isCatalogUrl = Boolean(textureUrl) && CATALOG_URLS.includes(textureUrl);

    const classified = useMemo(() => {
        if (!holesOn) {
            return {
                throughFace: [],
                blindFace: [],
                side: [],
                height: Number(detailData?.height) || 18,
                length: Number(detailData?.length) || 0,
                width: Number(detailData?.width) || 0,
            };
        }
        return classifyDetailHoles(detailData);
    }, [detailData, holesOn]);

    const classifiedGrooves = useMemo(() => {
        if (!groovesOn) {
            return {throughFace: [], blindFace: [], side: [], height};
        }
        return classifyDetailGrooves(detailData);
    }, [detailData, height, groovesOn]);

    const classifiedOther = useMemo(() => {
        if (!otherOn) {
            return {
                throughContours: [],
                blindContourPoses: [],
                throughRounds: [],
                blindRoundPoses: [],
                height,
            };
        }
        return classifyDetailOtherMachining(detailData);
    }, [detailData, height, otherOn]);

    const shape = useMemo(() => {
        const contour = buildContourShape(detailData);
        classified.throughFace.forEach((hole) => {
            contour.holes.push(
                new Path().absellipse(
                    Number(hole.center.x) || 0,
                    Number(hole.center.y) || 0,
                    Number(hole.radius),
                    Number(hole.radius),
                    0,
                    Math.PI * 2,
                ),
            );
        });
        [...classifiedGrooves.throughFace, ...classifiedGrooves.side].forEach((groove) => {
            const grooveShape = grooveContourShape(groove);
            if (grooveShape) contour.holes.push(grooveShape);
        });
        classifiedOther.throughContours.forEach((item) => {
            const otherShape = otherThroughContourShape(item);
            if (otherShape) contour.holes.push(otherShape);
        });
        classifiedOther.throughRounds.forEach((item) => {
            const path = otherThroughRoundPath(item);
            if (path) contour.holes.push(path);
        });
        return contour;
    }, [
        detailData,
        classified.throughFace,
        classifiedGrooves.throughFace,
        classifiedGrooves.side,
        classifiedOther.throughContours,
        classifiedOther.throughRounds,
    ]);

    const blindPoses = useMemo(
        () => classified.blindFace
            .map((hole) => getBlindFaceHolePose(hole, classified.height)),
        [classified.blindFace, classified.height],
    );

    const sidePoses = useMemo(
        () =>
            classified.side
                .map((hole) =>
                    getSideHolePose(hole, classified.length, classified.width, classified.height),
                )
                .filter(Boolean),
        [classified.side, classified.length, classified.width, classified.height],
    );

    const holeAxesPoses = useMemo(() => {
        if (!holesOn || !selected) return [];
        return [
            ...classified.throughFace,
            ...classified.blindFace,
            ...classified.side,
        ]
            .map((hole) => getHoleAxesPose(
                hole,
                classified.length,
                classified.width,
                classified.height,
            ))
            .filter(Boolean);
    }, [
        holesOn,
        selected,
        classified.throughFace,
        classified.blindFace,
        classified.side,
        classified.length,
        classified.width,
        classified.height,
    ]);

    const grooveBlindPoses = useMemo(
        () => classifiedGrooves.blindFace
            .map((groove) => getBlindFaceGroovePose(groove, classifiedGrooves.height))
            .filter((pose) => pose.shape),
        [classifiedGrooves.blindFace, classifiedGrooves.height],
    );

    const extrudeSettings = useMemo(
        () => ({
            steps: 1,
            depth: height,
            bevelEnabled: false,
            UVGenerator: createPanelUVGenerator(lengthMm, widthMm, height),
        }),
        [height, lengthMm, widthMm],
    );

    // Порядок панелей — только renderOrder (sN). polygonOffset фиксированный +1/+1,
    // как до priority-drawing: иначе заливка перекрывает MeshContourEdges и контур «мылится».
    const panelPolygonOffsetFactor = 1;
    const panelPolygonOffsetUnits = 1;

    const materialProps = {
        textureUrl,
        lengthMm,
        widthMm,
        scaleMm: textureScaleMm,
        rotationDeg: textureRotationDeg,
        selected,
        shellTransparent,
        polygonOffsetFactor: panelPolygonOffsetFactor,
        polygonOffsetUnits: panelPolygonOffsetUnits,
    };
    const texturedMaterial = isCatalogUrl ? (
        <CatalogPanelMaterial {...materialProps} />
    ) : (
        <ExternalPanelMaterial {...materialProps} />
    );

    const shellFallbackMaterial = (
        <meshStandardMaterial
            color={selected ? "#C8D4E8" : "#EAEBEA"}
            toneMapped={false}
            side={2}
            polygonOffset
            polygonOffsetFactor={panelPolygonOffsetFactor}
            polygonOffsetUnits={panelPolygonOffsetUnits}
            {...shellMaterialProps(shellTransparent)}
        />
    );

    return (
        <group
            position={[0, 0, widthMm * MM_TO_M]}
            rotation={[-Math.PI / 2, 0, 0]}
            scale={[MM_TO_M, MM_TO_M, MM_TO_M]}
            onClick={(event) => {
                if (!onSelect) {
                    return;
                }
                event.stopPropagation();
                onSelect(event);
            }}
        >
            <mesh
                renderOrder={renderOrder}
                onPointerDown={(event) => {
                    if (!onFreeRotationPointerDown) return;
                    onFreeRotationPointerDown(event);
                }}
            >
                <extrudeGeometry args={[shape, extrudeSettings]}/>
                {textureUrl ? (
                    <Suspense fallback={shellFallbackMaterial}>
                        <PanelTextureErrorBoundary
                            textureUrl={textureUrl}
                            fallback={shellFallbackMaterial}
                        >
                            {texturedMaterial}
                        </PanelTextureErrorBoundary>
                    </Suspense>
                ) : (
                    shellFallbackMaterial
                )}
                <MeshContourEdges color="black" threshold={1}/>
            </mesh>

            {classified.blindFace.map((hole, index) => (
                <group key={hole.id ?? `blind-group-${index}`}>
                    <BlindFaceEntranceDisk
                        hole={{
                            ...hole,
                            center: {
                                ...hole.center,
                                y: Number(hole.center?.y) || 0,
                            },
                        }}
                        heightMm={classified.height}
                        shellTransparent={shellTransparent}
                    />
                    <HoleCavityMesh pose={blindPoses[index]} shellTransparent={shellTransparent}/>
                </group>
            ))}

            {sidePoses.map((pose) => (
                <HoleCavityMesh
                    key={pose.key}
                    pose={pose}
                    shellTransparent={shellTransparent}
                />
            ))}

            {grooveBlindPoses.map((pose) => (
                <GrooveCavityMesh
                    key={pose.key}
                    pose={pose}
                    shellTransparent={shellTransparent}
                />
            ))}

            {classifiedOther.blindContourPoses.map((pose) => (
                <GrooveCavityMesh
                    key={pose.key}
                    pose={pose}
                    shellTransparent={shellTransparent}
                />
            ))}

            {classifiedOther.blindRoundPoses.map((pose) => (
                <group key={pose.key}>
                    <BlindFaceEntranceDisk
                        hole={{
                            ...pose.item,
                            center: {
                                ...pose.item.center,
                                y: Number(pose.item.center?.y) || 0,
                            },
                            contour: {
                                ...(pose.item.contour || {}),
                                position: pose.face,
                            },
                        }}
                        heightMm={classifiedOther.height}
                        shellTransparent={shellTransparent}
                    />
                    <HoleCavityMesh pose={pose} shellTransparent={shellTransparent}/>
                </group>
            ))}

            {holeAxesPoses.map((pose) => (
                <HoleMiniAxes
                    key={pose.key}
                    position={pose.position}
                    rotation={pose.rotation}
                    axisLength={pose.axisLength}
                />
            ))}
        </group>
    );
};

export default ModuleDetailMesh;
